import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { CheckCircle, XCircle, ArrowRight, Shield, DollarSign, Zap, AlertTriangle } from 'lucide-react';

interface CompetitorData {
  name: string;
  website: string;
  founded: string;
  tagline: string;
  intro: string;
  pricingThem: string;
  pricingUs: string;
  pricingExplain: string;
  prosTitle: string;
  pros: string[];
  consTitle: string;
  cons: string[];
  whoShouldUse: string;
  whoShouldUseLs: string;
  verdict: string;
  faqs: { q: string; a: string }[];
  features: Record<string, { them: string | boolean; us: string | boolean }>;
  targetKeywords: string[];
  relatedPosts: { slug: string; title: string }[];
}

const comparisons: Record<string, CompetitorData> = {
  linkbuilder: {
    name: 'LinkBuilder.com',
    website: 'linkbuilder.com',
    founded: '2021 (Codiny OU, Estonia)',
    tagline: 'The #1 Platform to Compare Prices for Guest Post & Sponsored Content',
    intro: 'LinkBuilder.com is the oldest and largest guest post price comparison tool on the market. Founded in 2021, it aggregates prices from 50+ sellers across a database of 339,000+ sites. It was the first tool to prove that the price comparison model works for backlinks  -  like Kayak or Google Flights, but for guest posts.',
    pricingThem: '$8 - $105/month (10 lookups free lifetime)',
    pricingUs: '$0 forever  -  unlimited everything',
    pricingExplain: 'LinkBuilder charges between $8 and $105 per month depending on the plan. Their free tier is extremely limited at just 10 lookups for life  -  after that, you must pay. LinkDiry is completely free with no limits. We earn referral fees from marketplaces when you click through, so our incentives align with yours: the better deals you find, the more we earn.',
    prosTitle: 'LinkBuilder.com Strengths',
    pros: [
      'Largest database in the market (339K sites)',
      '50+ sellers aggregated  -  widest marketplace coverage',
      '7+ SEO metrics (DR, TF, CF, DA, RefDomains, Traffic)',
      'Strong niche filtering including iGaming, CBD, and Crypto flags',
      'Established brand with 4+ years of track record',
      'Also offers managed link building service as an upsell',
    ],
    consTitle: 'LinkBuilder.com Weaknesses',
    cons: [
      'Expensive  -  $8-$105/month with only 10 free lookups ever',
      'Cluttered homepage that mixes marketing with the tool  -  requires scrolling',
      'UX feels dated compared to modern SaaS tools',
      'Too many filters visible at once overwhelm new users',
      'In testing, failed to find some major domains (only found Forbes out of 3 tested)',
      'Weekly updates  -  data can be 7 days stale',
      'No quality scores, no price alerts, no seller reviews',
    ],
    whoShouldUse: 'LinkBuilder.com is best for agencies with budget who need the absolute widest marketplace coverage and do not mind paying $105/month for the full feature set. If you are already paying for SEO tools and want one more subscription, LinkBuilder works.',
    whoShouldUseLs: 'LinkDiry is better for everyone else  -  freelancers, solo SEOs, small agencies, and anyone who wants the same data for free. You get quality scores, price alerts, side-by-side comparison, competitor scanning, SERP scanning, and daily updates that LinkBuilder does not offer at any price.',
    verdict: 'LinkBuilder.com proved the model works and has the data, but the execution leaves a massive opening. The dated UX, expensive pricing, and missing features (no quality scores, no price alerts, no seller ratings) mean you are paying more for less. LinkDiry offers a better product at $0.',
    faqs: [
      { q: 'Is LinkBuilder.com free?', a: 'LinkBuilder offers only 10 free lookups for life. After that, plans start at $8/month and go up to $105/month. LinkDiry is completely free with unlimited searches.' },
      { q: 'What is the best LinkBuilder alternative?', a: 'LinkDiry is the best free alternative to LinkBuilder.com. It offers the same 50+ seller coverage, plus quality scores, price alerts, competitor scanning, and SERP scanning  -  all for free.' },
      { q: 'Is LinkBuilder.com worth paying for?', a: 'Not when LinkDiry offers the same features for free. LinkBuilder charges up to $105/month for features that LinkDiry provides at no cost. The only advantage LinkBuilder has is its longer track record.' },
      { q: 'How does LinkBuilder.com compare to LinkDiry?', a: 'Both tools compare guest post prices from 50+ marketplaces. The key differences: LinkBuilder charges $8-$105/month, updates weekly, and lacks quality scores. LinkDiry is free, updates daily, and includes quality scores, price alerts, and side-by-side comparison.' },
    ],
    features: {
      'Database size': { them: '339K sites', us: '339K+ sites' },
      'Sellers compared': { them: '50+', us: '50+' },
      'Free tier': { them: '10 lookups lifetime', us: 'Unlimited  -  everything free' },
      'Monthly price': { them: '$8 - $105/mo', us: '$0 forever' },
      'SEO metrics': { them: '7+ (DR, TF, CF, DA)', us: '7+ (DR, DA, TF, CF)' },
      'Quality score': { them: false, us: true },
      'Price alerts': { them: false, us: true },
      'Competitor scanner': { them: false, us: true },
      'SERP scanner': { them: false, us: true },
      'Bulk check': { them: true, us: true },
      'Chrome extension': { them: false, us: true },
      'AI analysis': { them: false, us: true },
      'Seller reviews': { them: false, us: true },
      'Side-by-side compare': { them: false, us: true },
      'Update frequency': { them: 'Weekly', us: 'Daily' },
      'UX quality': { them: 'Cluttered, dated', us: 'Modern, search-first' },
    },
    targetKeywords: ['linkbuilder alternative', 'linkbuilder.com review', 'linkbuilder pricing', 'linkbuilder vs linkdiry'],
    relatedPosts: [
      { slug: 'link-building-pricing', title: 'Link Building Pricing 2026' },
      { slug: 'whitepress-alternatives', title: 'WhitePress Alternatives' },
      { slug: 'backlink-prices', title: 'Backlink Prices Explained' },
    ],
  },
  fatgrid: {
    name: 'FatGrid',
    website: 'fatgrid.com',
    founded: 'September 2025 (public beta)',
    tagline: 'Find the Best Price for Any Backlink',
    intro: 'FatGrid (formerly getlinks.pro) launched in September 2025. It calls itself "Google Flights for backlinks" and has a clean interface with some useful extras like competitor backlink scanning and Google SERP scanning. Their free core model is similar to ours.',
    pricingThem: 'Free core + credits for advanced tools',
    pricingUs: '$0 forever  -  unlimited everything',
    pricingExplain: 'FatGrid offers free core search but charges credits for advanced features: 60 credits per Google Scanner scan, 2 credits per domain for backlink scanning, and 1 credit per link for monitoring. LinkDiry is 100% free  -  no credits, no caps, no restrictions on any feature.',
    prosTitle: 'FatGrid Strengths',
    pros: [
      'Free core search  -  no subscription required',
      'Daily database updates (vs weekly for LinkBuilder)',
      'Useful extras: competitor backlink scanner + Google SERP scanner',
      'Language-agnostic search across all niches',
      'Modern, clean interface ("Google Flights for backlinks")',
      'Growing fast  -  340K+ publisher sites in database',
    ],
    consTitle: 'FatGrid Weaknesses',
    cons: [
      'Only 15 marketplaces (vs 50+ for LinkDiry and LinkBuilder)',
      'Very new  -  launched Sept 2025, only 7 months old, limited track record',
      'Credit system for advanced features can get expensive',
      'Fewer SEO metrics compared to established tools',
      'No quality scores for domains',
      'No price alerts or watchlist functionality',
      'No seller ratings or reviews',
    ],
    whoShouldUse: 'FatGrid is best for SEOs who want a modern interface and are specifically interested in competitor backlink scanning or Google SERP scanning features. The credit system works well for occasional use.',
    whoShouldUseLs: 'LinkDiry is better if you want 50+ sellers instead of 15, no credit system, quality scores, price alerts, and seller reviews. We cover 3x more marketplaces for the same price: $0.',
    verdict: 'FatGrid is our most similar competitor  -  both free, both modern. But FatGrid only covers 15 marketplaces vs our 50+, which means you are seeing prices from less than a third of available sellers. For the same domain, LinkDiry often finds cheaper options that FatGrid simply does not know about.',
    faqs: [
      { q: 'Is FatGrid free?', a: 'FatGrid offers free core search but charges credits for advanced features like SERP scanning, backlink scanning, and link monitoring. LinkDiry offers all features completely free with no credit system.' },
      { q: 'What is the best FatGrid alternative?', a: 'LinkDiry is the best FatGrid alternative. It covers 50+ marketplaces (vs FatGrid\'s 15), offers all features for free without credits, and includes quality scores and price alerts that FatGrid lacks.' },
      { q: 'How many marketplaces does FatGrid cover?', a: 'FatGrid covers 15+ marketplaces. LinkDiry covers 50+ marketplaces, giving you 3x more price data for every domain.' },
      { q: 'FatGrid vs LinkDiry  -  which is better?', a: 'LinkDiry offers wider marketplace coverage (50+ vs 15), no credit system, quality scores, price alerts, and seller reviews. FatGrid has competitor backlink scanning. For most users, LinkDiry provides better value.' },
    ],
    features: {
      'Database size': { them: '340K+ sites', us: '339K+ sites' },
      'Sellers compared': { them: '15+', us: '50+' },
      'Free tier': { them: 'Core search free', us: 'Unlimited  -  everything free' },
      'Monthly price': { them: 'Credits for advanced', us: '$0 forever' },
      'SEO metrics': { them: 'Moderate', us: '7+ (DR, DA, TF, CF)' },
      'Quality score': { them: false, us: true },
      'Price alerts': { them: false, us: true },
      'Competitor scanner': { them: true, us: true },
      'SERP scanner': { them: true, us: true },
      'Bulk check': { them: true, us: true },
      'Chrome extension': { them: false, us: true },
      'AI analysis': { them: false, us: true },
      'Seller reviews': { them: false, us: true },
      'Side-by-side compare': { them: false, us: true },
      'Update frequency': { them: 'Daily', us: 'Daily' },
      'UX quality': { them: 'Modern, clean', us: 'Modern, search-first' },
    },
    targetKeywords: ['fatgrid alternative', 'fatgrid review', 'fatgrid pricing', 'fatgrid vs linkdiry'],
    relatedPosts: [
      { slug: 'link-building-pricing', title: 'Link Building Pricing 2026' },
      { slug: 'collaborator-vs-whitepress', title: 'Collaborator vs WhitePress' },
      { slug: 'guest-post-seo', title: 'Guest Post SEO Guide' },
    ],
  },
  linkdetective: {
    name: 'LinkDetective.pro',
    website: 'linkdetective.pro',
    founded: 'Unknown',
    tagline: 'A Tool for Finding the Cheapest Links',
    intro: 'LinkDetective.pro is the simplest guest post price comparison tool on the market. It takes a minimalist approach: upload a list of domains, and it checks which ones sell guest posts and at what price. It is completely free and non-commercial  -  which means it gets minimal updates and has limited features.',
    pricingThem: '$0 (free, non-commercial)',
    pricingUs: '$0 forever  -  unlimited everything',
    pricingExplain: 'Both tools are free. The difference is that LinkDetective.pro is a side project with minimal development, while LinkDiry is a full-featured platform with daily updates, quality scores, price alerts, and 50+ seller coverage. Free does not mean equal.',
    prosTitle: 'LinkDetective.pro Strengths',
    pros: [
      'Completely free with no restrictions',
      'Clean, simple interface  -  zero learning curve',
      'Shows seller contact information for direct outreach (unique feature)',
      'Good for quick batch domain checks',
    ],
    consTitle: 'LinkDetective.pro Weaknesses',
    cons: [
      'Small database  -  only 118K sites (vs 339K+ for LinkDiry)',
      'Only 3 SEO metrics  -  cannot evaluate domain quality properly',
      'Cannot browse the full database  -  you must already know domains to check',
      'No discovery or niche exploration features',
      'No quality scores, no price alerts, no seller reviews',
      'Data updated infrequently (every 3-12 months)',
      'Excludes Casino, Dating, and Betting niches',
      'Feels like a side project, not a professional tool',
    ],
    whoShouldUse: 'LinkDetective.pro is fine for quick, one-off checks if you already have a specific list of domains and just want to see if they sell guest posts. Its simplicity is its only advantage.',
    whoShouldUseLs: 'LinkDiry is better for everything else. You get 3x the database, 7+ SEO metrics, quality scores, browse-by-niche discovery, bulk checking, price alerts, competitor scanning, SERP scanning, and daily updates  -  all still free.',
    verdict: 'LinkDetective.pro proves that simplicity has value, but it went too far into minimal territory. With only 118K sites, 3 metrics, and updates every 3-12 months, it is useful for quick checks but not for serious link building campaigns. LinkDiry offers the same simplicity plus everything LinkDetective is missing.',
    faqs: [
      { q: 'Is LinkDetective.pro free?', a: 'Yes, LinkDetective.pro is completely free. However, it has a much smaller database (118K vs 339K+), only 3 SEO metrics, and updates infrequently. LinkDiry is also free with a much larger database and more features.' },
      { q: 'What is the best LinkDetective alternative?', a: 'LinkDiry is the best LinkDetective.pro alternative. It has 3x the database, 7+ SEO metrics, quality scores, price alerts, and daily updates  -  all for free.' },
      { q: 'How does LinkDetective compare to LinkDiry?', a: 'LinkDetective has 118K sites and 3 metrics. LinkDiry has 339K+ sites, 7+ metrics, quality scores, price alerts, bulk check, competitor scanner, SERP scanner, and Chrome extension. Both are free.' },
    ],
    features: {
      'Database size': { them: '118K sites', us: '339K+ sites' },
      'Sellers compared': { them: 'Multiple', us: '50+' },
      'Free tier': { them: 'Completely free', us: 'Unlimited  -  everything free' },
      'Monthly price': { them: '$0', us: '$0 forever' },
      'SEO metrics': { them: '3 only', us: '7+ (DR, DA, TF, CF)' },
      'Quality score': { them: false, us: true },
      'Price alerts': { them: false, us: true },
      'Competitor scanner': { them: false, us: true },
      'SERP scanner': { them: false, us: true },
      'Bulk check': { them: true, us: true },
      'Chrome extension': { them: false, us: true },
      'AI analysis': { them: false, us: true },
      'Seller reviews': { them: false, us: true },
      'Side-by-side compare': { them: false, us: true },
      'Update frequency': { them: 'Every 3-12 months', us: 'Daily' },
      'UX quality': { them: 'Minimal, basic', us: 'Modern, search-first' },
    },
    targetKeywords: ['linkdetective alternative', 'linkdetective.pro review', 'linkdetective vs linkdiry'],
    relatedPosts: [
      { slug: 'how-much-do-backlinks-cost', title: 'How Much Do Backlinks Cost?' },
      { slug: 'link-building-cost', title: 'Link Building Cost Guide' },
      { slug: 'backlink-prices', title: 'Backlink Prices Explained' },
    ],
  },
};

export default function Compare() {
  const { competitor } = useParams<{ competitor: string }>();
  const data = comparisons[competitor || ''];

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <SEO
          title="LinkDiry vs Competitors  -  Free Alternative Comparison"
          description="See how LinkDiry compares to LinkBuilder, FatGrid, and LinkDetective. Feature-by-feature comparison of backlink price comparison tools. Free backlink price checker vs paid alternatives."
          canonical="/vs"
        />
        <TopNav />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Compare Backlink Price Comparison Tools</h1>
          <p className="text-slate-500 mb-8">See how our free backlink price checker stacks up against paid alternatives. Find the best link finder tool for your needs.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(comparisons).map(([key, val]) => (
              <Link key={key} to={`/vs/${key}`} className="p-6 border border-slate-200 rounded-2xl bg-white shadow-card hover:shadow-card-hover hover:border-brand-400 transition-all text-left">
                <h3 className="font-semibold text-slate-900 mb-1">vs {val.name}</h3>
                <p className="text-sm text-slate-500 mb-2">{val.tagline}</p>
                <p className="text-xs text-brand-600">See full comparison →</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const features = Object.keys(data.features);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`LinkDiry vs ${data.name}: Free Alternative Comparison [2026]`}
        description={`Compare LinkDiry vs ${data.name}  -  pricing, features, database size, and real differences. See which backlink price checker gives you more.`}
        canonical={`/vs/${competitor}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <TopNav />
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">
            LinkDiry vs {data.name}
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Side-by-side breakdown of two backlink price comparison tools. See which one gives you more for less.
          </p>
        </div>

        {/* Quick Summary Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="border border-slate-200 rounded-2xl p-5">
            <h3 className="font-bold text-slate-900 mb-1">{data.name}</h3>
            <p className="text-sm text-slate-500 mb-3">{data.tagline}</p>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{data.pricingThem}</span>
            </div>
          </div>
          <div className="border-2 border-brand-400 rounded-2xl p-5 bg-brand-50/30">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900">LinkDiry</h3>
              <span className="px-2 py-0.5 bg-brand-100 text-brand-700 rounded text-xs font-bold">YOU ARE HERE</span>
            </div>
            <p className="text-sm text-slate-500 mb-3">Free Backlink Price Checker  -  50+ Sellers</p>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-brand-600" />
              <span className="text-sm font-semibold text-brand-700">{data.pricingUs}</span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{data.name}: What It Does</h2>
          <p className="text-slate-600 leading-relaxed mb-3">{data.intro}</p>
          <p className="text-sm text-slate-500">Founded: {data.founded} | Website: {data.website}</p>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Pricing: {data.name} vs LinkDiry</h2>
          <p className="text-slate-600 leading-relaxed">{data.pricingExplain}</p>
        </section>

        {/* Feature Comparison Table */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Every Feature, Side by Side</h2>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase w-1/3">Feature</th>
                  <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase w-1/3">{data.name}</th>
                  <th className="px-5 py-4 text-center text-xs font-semibold text-brand-600 uppercase w-1/3 bg-brand-50">LinkDiry</th>
                </tr>
              </thead>
              <tbody>
                {features.map(feature => {
                  const themVal = data.features[feature].them;
                  const usVal = data.features[feature].us;
                  return (
                    <tr key={feature} className="border-b border-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-700">{feature}</td>
                      <td className="px-5 py-3 text-center">
                        {typeof themVal === 'boolean' ? (
                          themVal ? <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-slate-300 mx-auto" />
                        ) : <span className="text-slate-600">{themVal}</span>}
                      </td>
                      <td className="px-5 py-3 text-center bg-brand-50/30">
                        {typeof usVal === 'boolean' ? (
                          usVal ? <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /> : <XCircle className="w-5 h-5 text-slate-300 mx-auto" />
                        ) : <span className="font-semibold text-slate-900">{usVal}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pros & Cons */}
        <section className="grid md:grid-cols-2 gap-6 mb-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">{data.prosTitle}</h2>
            <ul className="space-y-2">
              {data.pros.map((pro, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {pro}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">{data.consTitle}</h2>
            <ul className="space-y-2">
              {data.cons.map((con, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {con}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Who Should Use */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Which Tool Fits Your Workflow</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-xl p-5">
              <h3 className="font-semibold text-slate-900 mb-2">Choose {data.name} if...</h3>
              <p className="text-sm text-slate-600">{data.whoShouldUse}</p>
            </div>
            <div className="border-2 border-brand-400 rounded-xl p-5 bg-brand-50/30">
              <h3 className="font-semibold text-brand-700 mb-2">Choose LinkDiry if...</h3>
              <p className="text-sm text-slate-600">{data.whoShouldUseLs}</p>
            </div>
          </div>
        </section>

        {/* Verdict */}
        <section className="mb-10">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-brand-600" />
              <h2 className="text-xl font-bold text-slate-900">Our Verdict</h2>
            </div>
            <p className="text-slate-600 leading-relaxed">{data.verdict}</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Common Questions</h2>
          <div className="space-y-3">
            {data.faqs.map((faq, i) => (
              <details key={i} className="group border border-slate-200 rounded-xl">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-slate-900 font-semibold text-sm hover:text-brand-600">
                  <h3>{faq.q}</h3>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">&#9662;</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-brand-500/20 gap-2">
            Try LinkDiry Free  -  No Signup Required <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-slate-400 mt-3">Search any domain instantly. Compare backlink prices from 50+ marketplaces.</p>
        </div>

        {/* Related Content */}
        <section className="border-t border-slate-100 pt-8">
          <h3 className="text-sm font-semibold text-slate-500 mb-3">Related Articles</h3>
          <div className="flex flex-wrap gap-2">
            {data.relatedPosts.map(post => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">
                {post.title}
              </Link>
            ))}
            <Link to="/marketplaces" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">
              Marketplace Comparison
            </Link>
            <Link to="/blog" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">
              All Blog Posts
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
